import { request } from '@/fetch/core';
import type {
  FetchGeocoderOptions,
  FetchGeocoderResult,
  GeocoderDetailAddress,
} from './geocoder.type';

declare global {
  interface Window {
    BMapGL: any;
  }
}

export interface Poi {
  label: string;
  value: string;
  address?: string;
}

export interface PoiSuggestion {
  id: string;
  title: string;
  address?: string;
  location?: { lat: number; lng: number };
}

export enum LocationType {
  gcj_02 = 'gcj-02',
  wgs_84 = 'wgs-84',
}

const formatPois = (poisList: Array<any>) => {
  return poisList?.map((item, index) => {
    return {
      value: item.lonlat + ':' + index,
      label: item.name + '-' + item.address,
    };
  });
};

const mapSearch = async (val: string | null) => {
  const postStr = {
    yingjiType: 1,
    keyWord: val,
    level: 18,
    mapBound: '-180,-90,180,90',
    queryType: 4,
    start: 0,
    count: 100,
    sourceType: 0,
  };
  const str = JSON.stringify(postStr);
  const tKey = 'dd515bb481c4d4c7f5b9056229e36e68';
  const url = `https://api.tianditu.gov.cn/v2/search?postStr=${str}&type=query&tk=${tKey}`;
  const resp = await window.fetch(url);
  const respBody = await resp.json();
  if (respBody?.resultType == 4) {
    return formatPois(respBody?.suggests) || [];
  }
  return [];
};

const normalizePoiItem = (item: any, index: number): PoiSuggestion => ({
  id: String(item.id ?? `${item.location?.lng}-${item.location?.lat}-${index}`),
  title: item.title || item.name || '',
  address: item.address || '',
  location: item.location
    ? {
        lng: Number(item.location.lng),
        lat: Number(item.location.lat),
      }
    : undefined,
});

const fetchPoiList = async (
  searchText: string,
  locationType = LocationType.gcj_02,
): Promise<any[]> => {
  const res = await request({
    method: 'GET',
    path: '/map/web/ld/place/search',
    params: {
      keyword: searchText,
      output: 'json',
    },
  });
  const list = Array.isArray(res?.data) ? res.data : [];
  return list.map((item, index) => {
    const poi = normalizePoiItem(item, index);
    if (locationType === LocationType.wgs_84 && poi.location) {
      const win = window as any;
      const locationWgs84 = win
        .coordtransform()
        .gcj02towgs84(poi.location.lng, poi.location.lat);
      return {
        label: poi.title,
        value: locationWgs84[0] + '-' + locationWgs84[1],
        address: poi.address,
      };
    }
    return locationType === LocationType.wgs_84
      ? poi
      : {
          ...poi,
          label: poi.title,
          value: `${poi.location?.lng}-${poi.location?.lat}`,
        };
  });
};

const fetchBaiduPoiList = (searchText: string): Promise<Poi[]> => {
  return new Promise((resolve) => {
    const keyword = searchText?.trim();
    if (!keyword || !window.BMapGL) {
      resolve([]);
      return;
    }
    const localSearch = new window.BMapGL.LocalSearch(keyword, {
      onSearchComplete: (result: any) => {
        try {
          const count = typeof result?.getNumPois === 'function' ? result.getNumPois() : 0;
          const list: Poi[] = [];
          for (let index = 0; index < count; index += 1) {
            const poi = result.getPoi(index);
            if (!poi?.point) {
              continue;
            }
            const title = poi.title || poi.name || '';
            const address = poi.address || poi.province || poi.city || '';
            list.push({
              label: title || address,
              value: `${poi.point.lng}-${poi.point.lat}`,
              address,
            });
          }
          resolve(list);
        } catch (_) {
          resolve([]);
        }
      },
    });
    localSearch.search(keyword);
  });
};

const fetchGeocoder = (
  location: string,
  options: FetchGeocoderOptions = {},
): Promise<FetchGeocoderResult> => {
  const { timeoutMs = 8000 } = options;
  const emptyResult: FetchGeocoderResult = {
    address: '',
    detailAddress: {},
  };

  return new Promise((resolve) => {
    try {
      if (!location || typeof location !== 'string' || !location.trim() || !window.BMapGL) {
        resolve(emptyResult);
        return;
      }
      const [latText, lngText] = location.split(',');
      const lat = Number(latText);
      const lng = Number(lngText);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        resolve(emptyResult);
        return;
      }
      const geocoder = new window.BMapGL.Geocoder();
      const point = new window.BMapGL.Point(lng, lat);
      const timer = window.setTimeout(() => {
        resolve(emptyResult);
      }, timeoutMs);
      geocoder.getLocation(point, (result: any) => {
        window.clearTimeout(timer);
        if (!result) {
          resolve(emptyResult);
          return;
        }
        const addressComponent = result.addressComponents || {};
        const recommend = result.address || '';
        const district = addressComponent.district ?? '';
        const street = addressComponent.street ?? '';
        const streetNumber = addressComponent.streetNumber ?? '';
        const fallbackAddress = `${district}${street}${streetNumber}`;
        const address = recommend || fallbackAddress || '';
        const detailAddress: GeocoderDetailAddress = {
          country: addressComponent.nation ?? '',
          province: addressComponent.province ?? '',
          city: addressComponent.city ?? '',
          district,
          street,
          streetNumber,
          name: recommend,
        };
        resolve({ address, detailAddress });
      });
    } catch (_) {
      resolve(emptyResult);
    }
  });
};

export { mapSearch, fetchPoiList, fetchBaiduPoiList, fetchGeocoder };
